import { Download, Copy, BookOpen } from 'lucide-react';

interface OASummaryArtDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  addToResources?: (resource: any) => void;
  showHeader?: boolean;
}

export default function OASummaryArtDisplay({
  result,
  theme = 'light',
  showToast,
  addToResources,
  showHeader = true
}: OASummaryArtDisplayProps) {
  // Parse result to extract 'output' if present
  const parseResult = (data: any) => {
    if (!data) return null;
    if (typeof data === 'object' && data.output) {
      return data.output;
    }
    return data;
  };

  const parsedResult = parseResult(result);

  // Check if this result contains office action prior art summary data
  if (!parsedResult || typeof parsedResult !== 'object') return null;

  // Check for key prior art summary properties (matching patentloop-frontend)
  const isOASummaryArt = parsedResult.references_summary !== undefined ||
                         parsedResult.key_teachings !== undefined ||
                         parsedResult.relevance_analysis !== undefined ||
                         parsedResult.combination_analysis !== undefined ||
                         parsedResult.distinguishing_features !== undefined ||
                         parsedResult.timeline_context !== undefined;

  if (!isOASummaryArt) return null;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast?.('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
      showToast?.('Failed to copy to clipboard');
    }
  };

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold">Prior Art Summary</h3>
        </div>
      )}

      <div className="space-y-4">
        {/* Important Dates */}
        {parsedResult.final_deadline && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <div className="text-sm">
              <span className="font-semibold text-red-900">Final Deadline:</span> {parsedResult.final_deadline}
            </div>
          </div>
        )}

        {/* References Summary */}
        {parsedResult.references_summary && (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <div className="flex items-start gap-2">
              <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-sm mb-2 text-blue-900">References Summary</h4>
                {Array.isArray(parsedResult.references_summary) ? (
                  <div className="space-y-3">
                    {parsedResult.references_summary.map((ref: any, idx: number) => (
                      <div key={idx} className="bg-white p-3 rounded border border-blue-300">
                        <div className="font-medium text-sm mb-1">{ref.reference || ref.citation || `Reference ${idx + 1}`}</div>
                        {ref.summary && <p className="text-sm mt-2">{ref.summary}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{parsedResult.references_summary}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Key Teachings */}
        {parsedResult.key_teachings && (
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-3 text-green-900">Key Teachings</h4>
            <div className="space-y-3">
              {typeof parsedResult.key_teachings === 'object' && !Array.isArray(parsedResult.key_teachings) ? (
                Object.entries(parsedResult.key_teachings).map(([refName, teachings]: [string, any]) => (
                  <div key={refName} className="bg-white p-3 rounded border border-green-300">
                    <div className="font-medium text-sm mb-2 text-green-900">{refName}</div>
                    {Array.isArray(teachings) ? (
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        {teachings.map((teaching: string, idx: number) => (
                          <li key={idx}>{teaching}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm">{String(teachings)}</p>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-sm whitespace-pre-wrap">
                  {typeof parsedResult.key_teachings === 'string'
                    ? parsedResult.key_teachings
                    : JSON.stringify(parsedResult.key_teachings, null, 2)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Relevance Analysis */}
        {parsedResult.relevance_analysis && (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-3 text-yellow-900">Relevance Analysis</h4>
            <div className="space-y-3">
              {typeof parsedResult.relevance_analysis === 'object' && !Array.isArray(parsedResult.relevance_analysis) ? (
                Object.entries(parsedResult.relevance_analysis).map(([refName, analysis]: [string, any]) => (
                  <div key={refName} className="bg-white p-3 rounded border border-yellow-300">
                    <div className="font-medium text-sm mb-2 text-yellow-900">{refName}</div>
                    {typeof analysis === 'object' ? (
                      <div className="space-y-2 text-sm">
                        {analysis.relevance_level && (
                          <div>
                            <span className="font-medium">Relevance Level:</span>{' '}
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              analysis.relevance_level === 'High' ? 'bg-red-100 text-red-800' :
                              analysis.relevance_level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {analysis.relevance_level}
                            </span>
                          </div>
                        )}
                        {analysis.relevant_claims && (
                          <div>
                            <span className="font-medium">Relevant Claims:</span> {Array.isArray(analysis.relevant_claims) ? analysis.relevant_claims.join(', ') : analysis.relevant_claims}
                          </div>
                        )}
                        {analysis.key_similarities && Array.isArray(analysis.key_similarities) && (
                          <div>
                            <span className="font-medium">Key Similarities:</span>
                            <ul className="list-disc list-inside ml-2 mt-1">
                              {analysis.key_similarities.map((sim: string, idx: number) => (
                                <li key={idx}>{sim}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {analysis.key_differences && Array.isArray(analysis.key_differences) && (
                          <div>
                            <span className="font-medium">Key Differences:</span>
                            <ul className="list-disc list-inside ml-2 mt-1">
                              {analysis.key_differences.map((diff: string, idx: number) => (
                                <li key={idx}>{diff}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm">{String(analysis)}</p>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-sm whitespace-pre-wrap">
                  {typeof parsedResult.relevance_analysis === 'string'
                    ? parsedResult.relevance_analysis
                    : JSON.stringify(parsedResult.relevance_analysis, null, 2)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Combination Analysis */}
        {parsedResult.combination_analysis && (
          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-3 text-purple-900">Combination Analysis</h4>
            {typeof parsedResult.combination_analysis === 'object' ? (
              <div className="space-y-2 text-sm">
                {parsedResult.combination_analysis.examiner_rationale && (
                  <div className="bg-white p-3 rounded border border-purple-300">
                    <span className="font-medium">Examiner's Rationale:</span>
                    <p className="mt-1">{parsedResult.combination_analysis.examiner_rationale}</p>
                  </div>
                )}
                {parsedResult.combination_analysis.combination_strength && (
                  <div>
                    <span className="font-medium">Combination Strength:</span>{' '}
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      parsedResult.combination_analysis.combination_strength === 'Strong' ? 'bg-red-100 text-red-800' :
                      parsedResult.combination_analysis.combination_strength === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {parsedResult.combination_analysis.combination_strength}
                    </span>
                  </div>
                )}
                {parsedResult.combination_analysis.motivation_to_combine && (
                  <div className="bg-white p-3 rounded border border-purple-300">
                    <span className="font-medium">Motivation to Combine:</span>
                    <p className="mt-1">{parsedResult.combination_analysis.motivation_to_combine}</p>
                  </div>
                )}
                {parsedResult.combination_analysis.potential_weaknesses && Array.isArray(parsedResult.combination_analysis.potential_weaknesses) && (
                  <div>
                    <span className="font-medium">Potential Weaknesses:</span>
                    <ul className="list-disc list-inside ml-2 mt-1">
                      {parsedResult.combination_analysis.potential_weaknesses.map((weakness: string, idx: number) => (
                        <li key={idx}>{weakness}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm whitespace-pre-wrap">
                {typeof parsedResult.combination_analysis === 'string'
                  ? parsedResult.combination_analysis
                  : JSON.stringify(parsedResult.combination_analysis, null, 2)}
              </div>
            )}
          </div>
        )}

        {/* Distinguishing Features */}
        {parsedResult.distinguishing_features && (
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-2 text-green-900">Distinguishing Features</h4>
            <div className="text-sm whitespace-pre-wrap">
              {Array.isArray(parsedResult.distinguishing_features) ? (
                <ul className="list-disc list-inside space-y-1">
                  {parsedResult.distinguishing_features.map((feature: string, idx: number) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              ) : typeof parsedResult.distinguishing_features === 'string' ? (
                parsedResult.distinguishing_features.split('\n').map((line: string, idx: number) => (
                  <div key={idx}>{line}</div>
                ))
              ) : (
                JSON.stringify(parsedResult.distinguishing_features, null, 2)
              )}
            </div>
          </div>
        )}

        {/* Timeline Context */}
        {parsedResult.timeline_context && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-3">Timeline Context</h4>
            {typeof parsedResult.timeline_context === 'object' ? (
              <div className="space-y-2 text-sm">
                {Object.entries(parsedResult.timeline_context).map(([key, value]: [string, any]) => (
                  <div key={key}>
                    <span className="font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>{' '}
                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm whitespace-pre-wrap">
                {typeof parsedResult.timeline_context === 'string'
                  ? parsedResult.timeline_context
                  : JSON.stringify(parsedResult.timeline_context, null, 2)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
